import { Modal } from '../../Modal';
import { Button, ScrollArea, Typography } from '../ui';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import type { OutreachPreviewCardData } from '../../../lib/outreach-preview-card';
import { OutreachPreviewCard } from '../chat-surface/MessageRow/OutreachPreviewCard';
import { ChoiceChips } from '../chat-surface/MessageRow/ChoiceChips';

// Dev-only gallery for the outreach preview card: the REAL component (never a copy) fed the wire shape
// the harness emits at its approval gate, one card per channel skin plus a mixed batch, so a designer
// can look at every state without driving a live conversation into it. Web mirror of the native Widget
// Gallery's outreach section. Chrome is English-only like the other dev panels; the cards' own words are
// what the server would send, here in English.

const LABELS = { badge: 'Symphony user', subject: 'Subject', from: 'From', files: 'Attached' };

const SAMPLES: { title: string; card: OutreachPreviewCardData }[] = [
  {
    title: 'WhatsApp · first message (intro frame)',
    card: {
      groups: [
        {
          channel: 'whatsapp',
          channelLabel: 'WhatsApp',
          entries: [
            {
              name: 'Dana Levi',
              relation: 'client',
              address: '+972 54-123-4567',
              stateLine: "First message to this number. WhatsApp delivers it inside Symphony's intro template.",
              delivery: 'wrapped',
              body: 'can we move Thursday to 16:00? The kiln run finishes late.',
              frameBefore: "Hi Dana, I'm Symphony, Noa's AI assistant. Noa sent you a message regarding Thursday: ",
              frameAfter: ". Reply here and I'll pass your response along.",
            },
          ],
        },
      ],
      labels: LABELS,
      actions: ['Send', 'Edit', "I'll introduce you first"],
    },
  },
  {
    title: 'WhatsApp · open thread (plain), Hebrew message',
    card: {
      groups: [
        {
          channel: 'whatsapp',
          channelLabel: 'WhatsApp',
          entries: [
            {
              name: 'יפעת אלקיים',
              relation: 'אחות',
              address: '+972 52-000-0011',
              stateLine: 'Their WhatsApp thread is open. The message goes exactly as written.',
              delivery: 'verbatim',
              body: 'היי יפעת, אפשר להזיז את חמישי ל-16:00? הקילן מסיים מאוחר.',
            },
          ],
        },
      ],
      labels: LABELS,
      actions: ['Send', 'Edit'],
    },
  },
  {
    title: 'SMS · first text (intro first, message follows) with segments',
    card: {
      groups: [
        {
          channel: 'sms',
          channelLabel: 'SMS',
          entries: [
            {
              name: 'Bob Rossi',
              address: '+972 54-000-0001',
              stateLine: 'First text to this number. A short intro goes first; your message follows once they reply.',
              delivery: 'contextual',
              leadTemplate:
                "Hi Bob, I'm Maestro, an AI assistant working with Noa from Adler Ceramics. They asked me to reach out regarding Thursday's delivery and share a message with you on their behalf. With your go-ahead, I'll share the message and keep things moving from there. Reply STOP if you'd prefer not to receive messages.",
              followsLabel: 'Once they reply, your message:',
              body: "Hi Bob, could the clay delivery come Thursday morning instead of Friday? We're firing on Friday and I'd like the new batch in before that.",
              footnote: '2 SMS segments',
            },
          ],
        },
      ],
      labels: LABELS,
      actions: ['Send', 'Edit', 'Change channel', "I'll introduce you first"],
    },
  },
  {
    title: 'Email · Symphony mailbox, with a file',
    card: {
      groups: [
        {
          channel: 'email',
          channelLabel: 'Email',
          entries: [
            {
              name: 'Alice Chen',
              address: 'alice@chenclay.com',
              stateLine: 'Sent from a Symphony mailbox, exactly as written.',
              delivery: 'verbatim',
              from: 'A Symphony mailbox',
              subject: 'Open evening on the 24th',
              body: "Hi Alice,\n\nWe're opening the studio on Friday the 24th at 18:30 and I'd love you to come. The agenda is attached.\n\nNoa",
              files: ['open-evening-agenda.pdf'],
            },
          ],
        },
      ],
      labels: LABELS,
      actions: ['Send', 'Edit'],
    },
  },
  {
    title: 'Gmail · from the user’s own address',
    card: {
      groups: [
        {
          channel: 'gmail',
          channelLabel: 'Gmail',
          entries: [
            {
              name: 'Carol Kim',
              relation: 'supplier',
              address: 'carol@kimglazes.com',
              stateLine: 'Sent from your Gmail, in your name.',
              delivery: 'verbatim',
              from: 'noa@adlerceramics.com',
              subject: 'Glaze order for October',
              body: 'Hi Carol, can we add two more tins of the celadon to the October order? Same delivery date works.\n\nThanks,\nNoa',
            },
          ],
        },
      ],
      labels: LABELS,
      actions: ['Send', 'Edit', 'Change channel'],
    },
  },
  {
    title: 'Symphony app · a Symphony user',
    card: {
      groups: [
        {
          channel: 'app',
          channelLabel: 'Symphony app',
          entries: [
            {
              name: 'Omer Adler',
              relation: 'brother',
              address: '+972 54-000-0002',
              symphonyUser: true,
              stateLine: 'Lands in their Symphony app, exactly as written. Their own assistant can act on it.',
              delivery: 'verbatim',
              body: 'Omer, Friday 18:30 at the studio, bring the folding tables?',
            },
          ],
        },
      ],
      labels: LABELS,
      actions: ['Send', 'Edit', 'Change channel'],
    },
  },
  {
    title: 'Batch · two WhatsApps on one row, one email on its own',
    card: {
      groups: [
        {
          channel: 'whatsapp',
          channelLabel: 'WhatsApp',
          entries: [
            {
              name: 'Dana Levi',
              address: '+972 54-123-4567',
              stateLine: 'Their WhatsApp thread is open. The message goes exactly as written.',
              delivery: 'verbatim',
              body: 'Dana, the open evening is Friday the 24th at 18:30. Are you in?',
            },
            {
              name: 'Gil Shani',
              address: '+972 52-000-0010',
              symphonyUser: true,
              stateLine: "No reply on this thread in the last 24 hours. WhatsApp delivers it inside Symphony's intro template.",
              delivery: 'wrapped',
              body: 'the open evening is Friday the 24th at 18:30. Are you in?',
              frameBefore: "Hi Gil, I'm Symphony, Noa's AI assistant. Noa sent you a message regarding the open evening: ",
              frameAfter: ". Reply here and I'll pass your response along.",
            },
          ],
        },
        {
          channel: 'email',
          channelLabel: 'Email',
          entries: [
            {
              name: 'Alice Chen',
              address: 'alice@chenclay.com',
              stateLine: 'Sent from a Symphony mailbox, exactly as written.',
              delivery: 'verbatim',
              from: 'A Symphony mailbox',
              subject: 'Open evening on the 24th',
              body: 'Hi Alice, the open evening is Friday the 24th at 18:30. Are you in?',
            },
          ],
        },
      ],
      labels: LABELS,
      actions: ['Send', 'Edit'],
    },
  },
];

const EMPTY = new Set<string>();

/** Gate (BrandHeader and DevMenu check it too): never renders without a dev build or a grant. */
export function DevOutreachPreviewPanel(props: { open: boolean; onClose: () => void }) {
  if (!devToolsUnlocked()) return null;
  return <OutreachPreviewPanel {...props} />;
}

function OutreachPreviewPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} width={640} title="Outreach preview cards" footer={<Button onClick={onClose}>Done</Button>}>
      <Typography variant="p3" color="secondary">
        The real card, fed the shape the send emits at its approval gate: one skin per channel, the WhatsApp intro frame around the user&apos;s words,
        the SMS intro that goes first, an email&apos;s from and subject, the Symphony badge, and a mixed batch. The chips under each are the
        reply&apos;s own choice chips, inert here.
      </Typography>
      <ScrollArea style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 16, maxHeight: '70vh' }}>
        {SAMPLES.map((sample) => (
          <section key={sample.title}>
            <Typography variant="p3" color="secondary" component="p" style={{ margin: '0 0 6px' }}>
              {sample.title}
            </Typography>
            <OutreachPreviewCard card={sample.card} />
            <ChoiceChips
              choices={{ askId: sample.title, options: sample.card.actions.map((label, i) => ({ id: `opt_${i + 1}`, label })) }}
              selectedIds={EMPTY}
            />
          </section>
        ))}
      </ScrollArea>
    </Modal>
  );
}
